import { Link } from 'react-router-dom';
import { Button } from '@lpr/ui';

// ── Types ───────────────────────────────────────────────────

export interface LandingPageContent {
  /** Eyebrow text above headline */
  eyebrow: string;
  /** Main headline — short, punchy */
  headline: string;
  /** Subhead — one sentence of empathy */
  subhead: string;
  /** Truth section — the raw, real paragraph */
  truth: string;
  /** Clarity section — what we actually do (3-5 bullet points) */
  clarityPoints: string[];
  /** Calm section — the closing reassurance */
  calm: string;
  /** CTA button text */
  ctaText: string;
  /** Optional: sidebar quote or stat */
  pullQuote?: string;
  /** SEO/tracking: utm_source value */
  utmSource: string;
}

// ── Component ───────────────────────────────────────────────

export default function LandingPage({ content }: { content: LandingPageContent }) {
  const reportUrl = `/report/request?utm_source=${content.utmSource}`;

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-hero-gradient text-white">
        <div className="mx-auto max-w-4xl px-4 py-20 sm:py-28 text-center space-y-6">
          <p className="text-lpr-gold font-medium tracking-wide text-sm uppercase">{content.eyebrow}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight max-w-3xl mx-auto">
            {content.headline}
          </h1>
          <p className="text-white/70 text-lg sm:text-xl max-w-2xl mx-auto leading-relaxed">
            {content.subhead}
          </p>
          <div className="pt-4">
            <Link to={reportUrl}>
              <Button variant="default" size="lg">{content.ctaText}</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Truth Section */}
      <section className="bg-bg-surface">
        <div className="mx-auto max-w-3xl px-4 py-16 sm:py-20">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6">Let's be real.</h2>
          <p className="text-text-secondary text-lg leading-relaxed whitespace-pre-line">
            {content.truth}
          </p>
        </div>
      </section>

      {/* Clarity Section */}
      <section className="bg-bg-primary">
        <div className="mx-auto max-w-4xl px-4 py-16 sm:py-20">
          <h2 className="text-2xl sm:text-3xl font-bold mb-8">Here's what we actually do.</h2>
          <div className="space-y-4">
            {content.clarityPoints.map((point, i) => (
              <div key={i} className="flex gap-4 items-start">
                <div className="flex-shrink-0 mt-1">
                  <svg className="h-5 w-5 text-lpr-teal" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                  </svg>
                </div>
                <p className="text-lg">{point}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pull Quote (optional) */}
      {content.pullQuote && (
        <section className="bg-lpr-teal/5 border-y border-v4-card-border">
          <div className="mx-auto max-w-3xl px-4 py-12 sm:py-16 text-center">
            <blockquote className="text-2xl sm:text-3xl font-display italic text-lpr-teal leading-relaxed">
              "{content.pullQuote}"
            </blockquote>
          </div>
        </section>
      )}

      {/* Four Strategies Preview */}
      <section className="bg-bg-surface">
        <div className="mx-auto max-w-5xl px-4 py-16 sm:py-20">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4">You have more options than you think.</h2>
          <p className="text-text-secondary text-center mb-10 max-w-2xl mx-auto">
            Your personalized report maps four distinct selling strategies — each with projected outcomes, timelines, and tradeoffs tailored to your property.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { title: 'Cash Offer', desc: 'Close in as few as 10 days. No showings. No open houses. No public exposure.', tag: 'For speed & privacy' },
              { title: 'List As-Is', desc: 'Market listing without renovations. Lower stress, transparent process.', tag: 'For simplicity' },
              { title: 'Cash Offer+', desc: 'Immediate cash plus a share of renovation profit. Certainty with upside.', tag: 'For speed & profit' },
              { title: 'Renovate & Sell', desc: 'Strategic upgrades with no upfront costs. Maximum return on your terms.', tag: 'For max profit' },
            ].map((s) => (
              <div key={s.title} className="rounded-2xl border border-v4-card-border bg-v4-card-bg p-6 space-y-2">
                <span className="text-xs font-medium text-lpr-teal uppercase tracking-wide">{s.tag}</span>
                <h3 className="text-lg font-semibold">{s.title}</h3>
                <p className="text-sm text-text-secondary">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Calm / CTA Section */}
      <section className="bg-hero-gradient text-white">
        <div className="mx-auto max-w-3xl px-4 py-16 sm:py-20 text-center space-y-6">
          <p className="text-white/70 text-lg sm:text-xl leading-relaxed max-w-2xl mx-auto whitespace-pre-line">
            {content.calm}
          </p>
          <div className="pt-2">
            <Link to={reportUrl}>
              <Button variant="default" size="lg">{content.ctaText}</Button>
            </Link>
          </div>
          <p className="text-white/40 text-sm">Private. No obligation. No pressure.</p>
        </div>
      </section>
    </div>
  );
}
